import { EntityInvitationModel, useRestApiProvider } from "@jugl-web/rest-api";
import { FC, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  PageLoaderFull,
} from "@jugl-web/ui-components/cross-platform";
import { useToast, useTranslations } from "@jugl-web/utils";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { useShowAlert } from "@jugl-web/domain-resources/ui-components";
import { useNavigateToEntityById } from "@web-src/features/app/hooks/useNavigateToEntityById";
import { useHandleInvitationError } from "@web-src/modules/common/hooks/useHandleInvitationError";
import get from "lodash/get";
import { InviteError } from "@web-src/modules/common/types/InviteError";
import { ReactComponent as TrashIcon } from "./assets/trash-icon.svg";
import { ListItemCard } from "../../pages/EntitySelectPage/components/ListItemCard";
import alertAnimation from "./assets/alert.json";
import { ExpiredInvitationAlert } from "../../pages/EntitySelectPage/components/ExpiredInvitationAlert/ExpiredInvitationAlert";
import { ReactComponent as RightArrowIcon } from "./assets/right-arrow.svg";
import { ReactComponent as BlackCheckIcon } from "./assets/black-check.svg";
import brokenHeartAnimation from "./assets/broken-heart.json";

type EntityInvitationCardProps = {
  invitation: EntityInvitationModel;
  disableDelete?: boolean;
  className?: string;
  isJoined?: boolean;
  variant?: "default" | "entity_link";
  isArrowHidden?: boolean;
  redirectOnClick?: boolean;
  onInvitationAcceptedSuccess?: () => void;
};

export const EntityInvitationCard: FC<EntityInvitationCardProps> = (props) => {
  const {
    invitation: {
      entityName,
      entityImg,
      inviterAvatar,
      inviterName,
      id,
      entityId,
      type,
    },
    disableDelete,
    redirectOnClick,
    className,
    isJoined,
    variant,
    isArrowHidden,
    onInvitationAcceptedSuccess,
  } = props;
  const { t } = useTranslations();
  const [rejectInvitationAlertIsOpen, setRejectInvitationAlertIsOpen] =
    useState(false);
  const [expiredInvitationAlertIsOpen, setExpiredInvitationAlertIsOpen] =
    useState(false);
  const showAlert = useShowAlert();

  const { entitiesApi } = useRestApiProvider();
  const [respondToInvitation, { isLoading }] =
    entitiesApi.useRespondToEntityInviteMutation();
  const [acceptLinkInvitation, { isLoading: isAcceptingLinkInvitation }] =
    entitiesApi.useAcceptEntityInvitationByLinkMutation();
  const { toast } = useToast({ variant: "web" });
  const navigateToEntity = useNavigateToEntityById();
  const { isInviteError, handleError } = useHandleInvitationError();
  const handleAcceptInvitationClick = async () => {
    const response =
      variant === "entity_link"
        ? await acceptLinkInvitation({
            inviteId: id,
            entityId,
            silentError: true,
          })
        : await respondToInvitation({
            inviteId: id,
            action: "accept",
            silentError: true,
          });
    if ("error" in response && response.error) {
      const error = get(
        response.error,
        "data.errors",
        "Unknown"
      ) as InviteError;
      if (isInviteError(error)) {
        handleError("invite", error);
        return;
      }
      showAlert({
        img: (
          <Lottie
            options={{ animationData: brokenHeartAnimation }}
            height={239}
            width={357}
          />
        ),
        title: "Invitation not found 😱",
        content:
          "We couldn’t open this link. It might be invalid, expired, or the item has been removed.",
        buttons: [
          {
            text: "Okay",
            role: "close",
            color: "primary",
          },
        ],
      });
      return;
    }

    onInvitationAcceptedSuccess?.();
  };

  const handleRemoveInvitation = async () => {
    const response = await respondToInvitation({
      inviteId: id,
      action: "decline",
    });
    if ("error" in response && response.error) {
      return;
    }
    toast(
      t({
        id: "feedback.successfully-declined-workspace-invitation",
        defaultMessage: "Invitation declined successfully",
      })
    );

    setRejectInvitationAlertIsOpen(false);
  };

  return (
    <>
      <PageLoaderFull
        isActive={isLoading || isAcceptingLinkInvitation}
        isTransparent
      />
      <ListItemCard
        onClick={
          redirectOnClick
            ? () => {
                navigateToEntity(entityId, type === "org");
              }
            : undefined
        }
        title={entityName}
        avatarSrc={entityImg}
        subTitle={`Invitation from ${inviterName}`}
        subAvatarSrc={inviterAvatar}
        subAvatarTitle={inviterName}
        className={className}
        rightContent={
          <div className="flex items-center gap-4">
            {!isJoined && (
              <Button
                size="small"
                onClick={() => {
                  handleAcceptInvitationClick();
                }}
                iconEnd={
                  isArrowHidden ? undefined : (
                    <RightArrowIcon className="ml-1" />
                  )
                }
              >
                Join
              </Button>
            )}
            {!disableDelete && (
              <Button
                size="small"
                onClick={() => setRejectInvitationAlertIsOpen(true)}
                color="grey"
                className="px-2"
              >
                <TrashIcon />
              </Button>
            )}
            {isJoined && (
              <div className="font-primary relative px-6 py-1 text-sm font-semibold">
                <div className="absolute inset-0 rounded-md bg-gradient-to-r from-[#2E17FF] to-[#FFAAFE]" />

                <div className="absolute inset-[2px] rounded-md bg-white" />

                <div className="text-dark-A100 relative z-10 flex items-center justify-center gap-1">
                  {t({ id: "common.joined", defaultMessage: "Joined" })}
                  <BlackCheckIcon />
                </div>
              </div>
            )}
          </div>
        }
      />
      <ExpiredInvitationAlert
        isOpen={expiredInvitationAlertIsOpen}
        onClose={() => setExpiredInvitationAlertIsOpen(false)}
      />

      <Alert
        isOpen={rejectInvitationAlertIsOpen}
        onRequestClose={() => setRejectInvitationAlertIsOpen(false)}
        img={
          <div className="relative grid h-[150px] w-[150px]">
            <Avatar imageUrl={entityImg} username={entityName} size="5xl" />
            <div className="absolute bottom-0 right-0 flex h-[60px] w-[60px] items-center justify-center rounded-full bg-white">
              <Lottie
                options={{ animationData: alertAnimation }}
                height={40}
                width={40}
              />
            </div>
          </div>
        }
        header="Confirm Action"
        title="Reject Invitation"
        isCloseButtonVisible
        content={
          <span>
            You are trying to reject invitation to the Workspace{" "}
            <span className="text-primary-800 font-semibold">{entityName}</span>
            . This action can&apos;t be undone
          </span>
        }
        buttons={[
          {
            text: "Reject Invitation",
            color: "tertiary",
            onClick: () => handleRemoveInvitation(),
          },
        ]}
      />
    </>
  );
};
